/**
 * Booking experimenter config (case study for skip-until-args-ready; see doc).
 * frontend/docs/duplicative-api-loading.md
 */
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router-dom'

import { useGetExperimenterConfigQuery, eventTypeSelector, rootApi } from 'root-api'
import { currentUserSelector, calAnonymousIdSelector } from 'selectors/user'
import { useIdentityReady } from 'contexts/IdentityContext'

// Keep in sync with PROFILE_FETCH_PATHS in components/use_profile.js
const PROFILE_SLUG_ROUTES = [
  { path: '/:profileSlug', exact: true },
  { path: '/:profileSlug/invoices/:invoiceUuid', exact: true },
  { path: '/:profileSlug/packages/:packageUuid', exact: true },
  { path: '/:profileSlug/payments/:packageUuid', exact: true },
  {
    path: '/:profileSlug/packages/:packageUuid/purchase/:purchasedPackageUuid',
    exact: true,
  },
  {
    path: '/:profileSlug/payments/:packageUuid/purchase/:purchasedPackageUuid',
    exact: true,
  },
]

const SCHEDULING_LINK_ROUTE = { path: '/d/:linkUid/:eventTypeName?', exact: false }

const getProfileSlugFromPath = (pathname = window.location.pathname) => {
  const match = PROFILE_SLUG_ROUTES.map((route) => matchPath(pathname, route)).find(Boolean)
  return match?.params?.profileSlug ?? null
}

const isSchedulingLinkPath = (pathname = window.location.pathname) => (
  Boolean(matchPath(pathname, SCHEDULING_LINK_ROUTE))
)

const selectInitialSettingsRequest = (state) => (
  rootApi.endpoints.getInitialSettings.select()(state)?.data?.request
)

export const useBookingExperimenterConfig = () => {
  const eventType = useSelector(eventTypeSelector)
  const initialSettingsRequest = useSelector(selectInitialSettingsRequest)
  const currentUser = useSelector(currentUserSelector)
  const calAnonymousId = useSelector(calAnonymousIdSelector)
  const isIdentityReady = useIdentityReady()

  const profileSlug = getProfileSlugFromPath()
  const onSchedulingLinkPath = isSchedulingLinkPath()

  const profileFromCache = useSelector((state) => (
    profileSlug
      ? rootApi.endpoints.getProfile.select({ slug: profileSlug })(state)?.data
      : undefined
  ))

  const landingPageFromCache = useSelector((state) => {
    if (!onSchedulingLinkPath) return undefined

    const schedulingLink = rootApi.endpoints.getSchedulingLink.select()(state)?.data
    if (schedulingLink?.owner_type !== 'LandingPage') return undefined

    return rootApi.endpoints.getLandingPage.select({
      uuid: schedulingLink.owner_uuid,
    })(state)?.data
  })

  const routingFormFromCache = useSelector((state) => {
    if (!onSchedulingLinkPath) return undefined

    const schedulingLink = rootApi.endpoints.getSchedulingLink.select()(state)?.data
    if (schedulingLink?.owner_type !== 'FormBuilder::Form') return undefined

    return rootApi.endpoints.getRoutingForm.select({
      uuid: schedulingLink.owner_uuid,
    })(state)?.data
  })

  // Org/locale for experiments = host (event type, profile, landing page, form)—not invitee fields.
  // See frontend/docs/duplicative-api-loading.md § Organization vs locale.
  const organizationUuid = eventType?.organization?.uuid
    ?? profileFromCache?.organization_uuid
    ?? landingPageFromCache?.organization_uuid
    ?? routingFormFromCache?.organization?.uuid

  const locale = eventType?.locale
    ?? profileFromCache?.locale
    ?? landingPageFromCache?.locale
    ?? routingFormFromCache?.locale
    ?? initialSettingsRequest?.locale

  return useGetExperimenterConfigQuery(
    {
      organizationUuid,
      locale,
      ...(currentUser?.uuid && { userId: currentUser.uuid }),
      ...(calAnonymousId && { anonymousId: calAnonymousId }),
    },
    {
      skip: !isIdentityReady || !organizationUuid || !locale,
    },
  )
}
